import { Stack, Text, Title, Button, Box, ScrollArea } from '@mantine/core'
import { IconArrowRight } from '@tabler/icons-react'

/**
 * Title card shown when a player enters a new thread. Orients them that a new
 * thread (chapter) has begun before its first beat. Shows the chapter number,
 * the thread name, and an optional author-written description
 * (thread.options.description, edited in bubot_admin). The Next button dismisses
 * the card and reveals the thread's first beat.
 *
 * Layout: the chapter label / title / description live in a scrollable region;
 * the Next button is pinned to the bottom so it never scrolls offscreen no
 * matter how long the description is. A short card stays vertically centered;
 * a long one scrolls (with a visible scrollbar) and keeps the button reachable.
 */
export function ThreadTitleCard({ thread, threadIndex, threadCount, onNext }) {
  const description = thread?.options?.description?.trim()

  return (
    <Box
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '1.5rem 2rem 1.25rem',
        minHeight: 0,
      }}
    >
      {/* Scrollable body — centered when short, scrolls when tall */}
      <ScrollArea
        style={{ flex: 1, minHeight: 0, width: '100%' }}
        type="auto"
        scrollbarSize={8}
        offsetScrollbars
      >
        <Box
          style={{
            minHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Stack align="center" gap="lg" maw={720} py="md" style={{ textAlign: 'center', width: '100%' }}>
            <Text
              size="sm"
              c="cyan.4"
              ff="Orbitron, sans-serif"
              tt="uppercase"
              lts={3}
              fw={600}
            >
              Chapter {threadIndex + 1} of {threadCount}
            </Text>

            <Box
              style={{
                width: 80,
                height: 2,
                background: 'linear-gradient(to right, transparent, rgba(0, 229, 255, 0.6), transparent)',
              }}
            />

            <Title
              order={1}
              ff="Orbitron, sans-serif"
              c="cyan.2"
              className="glow-text-cyan"
              style={{ fontSize: 'clamp(1.5rem, 4vw, 2.25rem)', lineHeight: 1.2 }}
            >
              {thread?.name || `Chapter ${threadIndex + 1}`}
            </Title>

            {description && (
              <Text
                c="gray.3"
                style={{
                  lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                  // Scale with the player's content base + S/M/L size pick (and
                  // honor the min-size floor), same as lesson copy.
                  fontSize: 'calc(var(--bw-content-base, 1.125rem) * var(--bw-content-scale, 1))',
                }}
              >
                {description}
              </Text>
            )}
          </Stack>
        </Box>
      </ScrollArea>

      {/* Pinned footer — the action button stays onscreen regardless of length */}
      <Box style={{ flexShrink: 0, display: 'flex', justifyContent: 'center', paddingTop: '0.75rem' }}>
        <Button
          size="md"
          rightSection={<IconArrowRight size={18} />}
          onClick={onNext}
          className="glow-cyan"
          ff="Orbitron, sans-serif"
        >
          {threadIndex === 0 ? 'Begin' : 'Continue'}
        </Button>
      </Box>
    </Box>
  )
}
